/**
 * AI.GEN lines=1-120
 * 通用工具函数
 */
import { message } from 'antd';

import type { OrderBaseInfoResp } from '@/apis/order/declare';
import i18n from '@/locales/i18n';

/**
 * AI.GEN lines=1-12
 * 下载文件
 * @param res 资源 blob
 * @param filename 文件名
 */
export const downloadFile = (res: Blob, filename: string): void => {
  const a = document.createElement('a');
  const URL = window.URL || window.webkitURL;
  const href = URL.createObjectURL(res);
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(href);
};

/**
 * 下载图片
 * @param url 图片 URL
 * @param filename 文件名
 */
export const downloadImage = (url: string, filename: string): void => {
  const img = new Image();
  const time = `_t=${Date.now()}`;

  img.crossOrigin = 'anonymous';
  img.src = `${url}${url.indexOf('?') === -1 ? '?' : '&'}${time}`;
  img.addEventListener('load', () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    canvas.width = img.width;
    canvas.height = img.height;

    // 将图片绘制到 canvas 上
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // 将 canvas 内容转换为 Blob 并触发下载
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('无法生成 Blob');
        return;
      }

      downloadFile(blob, filename);
    }, 'image/png');
  });
  img.addEventListener('error', () => {
    message.error(i18n.t('error.imageLoadError'));
  });
};

/**
 * AI.GEN lines=1-12
 * 判断是否为朴素对象（plain object）
 * @param obj 要判断的对象
 * @returns 是否为朴素对象
 */
export const isPlainObject = <T = unknown>(
  obj: unknown,
): obj is Record<string, T> => {
  if (typeof obj !== 'object' || obj === null) return false;

  let proto = obj;
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }

  return Object.getPrototypeOf(obj) === proto;
};

/**
 * AI.GEN lines=1-10
 * 对象转数组
 * @param obj 要转换的对象
 * @returns 选项数组
 */
export const objectToArray = (
  obj: Record<string | number, string | number>,
): Array<{ label: string | number; value: string | number }> => {
  return Object.entries(obj).map(([value, label]) => {
    return {
      label,
      value,
    };
  });
};

/**
 * AI.GEN lines=1-8
 * 获取非空数组
 * @param list 数组
 * @returns 非空数组或 undefined
 */
export const getNonEmptyList = <T>(list?: T[]): T[] | undefined => {
  if (!list?.length) {
    return undefined;
  }

  return list;
};

/**
 * AI.GEN lines=1-18
 * 构建 orderSnList（用于合单相关操作）
 * @param mainOrder 主订单
 * @param orders 其他订单列表
 * @returns 去重后的订单号数组
 */
export const buildOrderSnList = (
  mainOrder: OrderBaseInfoResp,
  orders: OrderBaseInfoResp[],
): string[] => {
  const orderSnList: string[] = [];

  if (mainOrder.orderSn) {
    orderSnList.push(mainOrder.orderSn);
  }

  orders.forEach((order) => {
    if (order.orderSn && !orderSnList.includes(order.orderSn)) {
      orderSnList.push(order.orderSn);
    }
  });

  return orderSnList;
};
